import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Hook to get selfgrade setting from Firestore in real-time
 * 
 * Reads from: system/config.selfgrade
 * 
 * When selfgrade is true (default):
 * - For AIs > 2: Each model grades itself + peers (self included in candidates)
 * - Grade matrix diagonal shows actual grades (not 'x')
 * - Self-grades are included in majority judgment calculations
 * 
 * When selfgrade is false:
 * - For AIs > 2: Each model grades only peers (self excluded from candidates)
 * - Grade matrix diagonal shows 'x'
 * - Self-grades are excluded from majority judgment calculations
 * 
 * Note: T=2 always includes self-grading regardless of this setting
 * (minimum 2 candidates required)
 * 
 * Rationale: Including self-grades eliminates systematic bias where some
 * providers (e.g., Google) grade more generously than others (e.g., OpenAI).
 * By including self-grades, each model's "generosity" applies equally to
 * itself and others, neutralizing this asymmetric effect.
 * 
 * @returns { selfgrade: boolean, loading: boolean }
 */
export function useSelfGrade(): { selfgrade: boolean; loading: boolean } {
  const [selfgrade, setSelfgrade] = useState<boolean>(true); // Default to ON (new behavior)
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Listen to system/config document for real-time updates
    const configRef = doc(db, 'system', 'config');
    
    const unsubscribe = onSnapshot(
      configRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          // Default to true if field doesn't exist
          const mode = data?.selfgrade !== false;
          setSelfgrade(mode);
          console.log('🔧 SELFGRADE loaded from Firestore:', mode);
        } else {
          // Document doesn't exist, default to ON (true)
          setSelfgrade(true);
          console.log('⚠️ system/config not found, defaulting SELFGRADE to true');
        }
        setLoading(false);
      },
      (error) => {
        console.error('❌ Error loading SELFGRADE from Firestore:', error);
        setSelfgrade(true); // Default to ON on error
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { selfgrade, loading };
}





